<template>
	<div v-if="previewUrl" class="attached-image">
		<img :src="previewUrl" class="attached-image-img" alt="" />
		<button v-if="showRemoveFile" class="attached-image-close" @click="removeFile">
			<svg-icon name="cross" :size="6" />
		</button>
	</div>
	<div v-else class="attached-file">
		<icon-file-type class="attached-file-icon" :typeFile="fileExtension" />
		<div>
			<p class="attached-file-name">{{ shortenedName }}</p>
			<p class="attached-file-size">{{ formattedSize }}</p>
		</div>
		<button v-if="showRemoveFile" class="attached-file-close" @click="removeFile">
			<svg-icon name="cross" :size="6" />
		</button>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import SvgIcon from './SvgIcon.vue'
import IconFileType from '@/components/ui/IconFileType.vue'

const props = defineProps({
	name: { type: String, default: '' },
	size: { type: Number, default: 0 },
	mimeType: { type: String, default: 'txt' },
	showRemoveFile: { type: Boolean, default: false },
	previewUrl: { type: String, default: '' }
})

const emit = defineEmits<{
	removeFile: []
}>()

const formattedSize = computed(() => {
	const { size } = props
	if (size < 1024) return `${size} Б`
	if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} КБ`
	return `${(size / (1024 * 1024)).toFixed(2)} МБ`
})

const fileExtension = computed(() => {
	const lastDotIndex = props.name.lastIndexOf('.')
	return props.name.slice(lastDotIndex + 1)
})

const shortenedName = computed(() => {
	const maxLength = 28
	const { name } = props
	if (name.length <= maxLength) return name

	const lastDotIndex = name.lastIndexOf('.')
	if (lastDotIndex === -1) return `${name.slice(0, maxLength - 3)}...`

	const extension = name.slice(lastDotIndex)
	const maxNameLength = maxLength - extension.length - 3

	if (maxNameLength <= 0) return `...${extension}`

	return `${name.slice(0, lastDotIndex).slice(0, maxNameLength)}... ${extension}`
})

const removeFile = () => emit('removeFile')
</script>

<style lang="scss" scoped>
.attached-image {
	position: relative;
	width: 80px;
	height: 80px;
	margin-bottom: 8px;
	&-img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		border-radius: var(--radius-md);
	}
	&-close {
		position: absolute;
		width: 16px;
		height: 16px;
		border: none;
		border-radius: var(--radius-lg);
		background-color: var(--overlay);
		color: var(--text-inverse);
		top: 4px;
		right: 4px;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: $transition-bg;
		&:hover {
			background-color: var(--overlay);
		}
		&:active {
			background-color: var(--overlay);
		}
	}
}
.attached-file {
	max-width: 320px;
	padding: 6px;
	border-radius: var(--radius-md);
	border: 1px solid var(--border-subtle);
	display: flex;
	align-items: center;
	margin-bottom: 8px;
	position: relative;
	&-icon {
		margin-right: 6px;
	}
	&-name {
		font: var(--font-subtitle);
		color: var(--text-primary);
	}
	&-size {
		font: var(--font-caption);
		color: var(--text-secondary);
	}
	&-close {
		position: absolute;
		width: 12px;
		height: 12px;
		border: none;
		border-radius: var(--radius-md);
		background-color: var(--overlay);
		color: var(--text-inverse);
		top: 4px;
		right: 4px;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: $transition-bg;
		&:hover {
			background-color: var(--overlay);
		}
		&:active {
			background-color: var(--overlay);
		}
	}
}
</style>
